/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import FilterDrawer, {FilterDrawerCategory} from '@ion/dls/src/components/drawer/FilterDrawer';
import {getFilterDrawerWidth} from '@ion/dls/src/components/drawer/utils';
import {dlsSidebarDrawerLeft} from '@ion/dls/src/components/navigation/NavigationStore';
import {AppliedFilterBarFavoriteState} from '@ion/dls/src/filters/AppliedFilterBarTypes';
import {afterNextPaint} from '@ion/dls/src/filters/useFilterTreeChecklist';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import TimeModeLimitWarning from '@ion/dls/src/maps/components/timeMode/current/TimeModeLimitWarning';
import {dlsLayout} from '@ion/dls/src/theme/theme';
import {BoundaryMode} from '@ion/dls/src/types/types';
import {useApplyHistoryTimeMode} from '@/routes/tracking/hooks/history/useApplyHistoryTimeMode';
import {useReturnToLiveTracking} from '@/routes/tracking/hooks/history/useReturnToLiveTracking';
import {useSelectAssetAndResets} from '@/routes/tracking/hooks/sidesheet/useSelectAssetAndResets';
import useBoundaryFilter from '@/routes/tracking/hooks/tracking/useBoundaryFilter';
import {applyAssetHistoryDateFilter} from '@/store/actions/reports';
import {applyBoundaryFilter, setBoundaryFilterMode, setSelectedFiltersActive} from '@/store/actions/tracking';
import {
  assetDetailViewSelector,
  boundaryFilterSelector,
  boundaryModeSelector,
  historyFromSelector,
  historyPresetSelector,
  historyToSelector
} from '@/store/selectors/tracking-selectors';
import {FavoriteDialogLabels, FavoriteDialogState, FavoriteDialogType} from './favoriteDialogTypes';
import {
  areTrackingFavoriteStatesEqual,
  areTrackingFavoriteWidgetsEqual,
  getTrackingFavoriteHistoryRange,
  getTrackingFavoriteSelectedCategoryIds,
  getTrackingFavoriteWidgetState,
  hasTrackingFavoriteBoundary,
  isTrackingFavoriteLive,
  normalizeTrackingFavoriteState,
  TrackingFavoriteNameValidationError,
  trimTrackingFavoriteName,
  validateTrackingFavoriteName
} from './favoriteUtils';
import {
  deleteTrackingFilterFavorite,
  getTrackingAppliedFavorite,
  getTrackingFilterCategoryOrder,
  getTrackingFilterExpandedIds,
  getTrackingFilterFavorites,
  getTrackingWorkingFavorite,
  initializeTrackingFilterCategoryOrder,
  initializeTrackingFilterFavorites,
  renameTrackingFilterFavorite,
  saveTrackingFilterFavorite,
  setTrackingAppliedFavorite,
  setTrackingFilterDrawerOpen,
  setTrackingFilterExpandedIds,
  setTrackingWorkingFavorite,
  subscribeTrackingFilterCategoryOrder,
  subscribeTrackingFilterExpandedIds,
  subscribeTrackingFilterFavorites,
  subscribeTrackingWorkingFavorite
} from './store';
import TrackingFavoritesFilter from './TrackingFavoritesFilter';
import TrackingFilterDrawerDialogs from './TrackingFilterDrawerDialogs';
import {
  TrackingFavoriteFilterState,
  TrackingFilterDrawerCategoryId,
  TrackingFilterDrawerHandle,
  TrackingFilterNavigationRequest
} from './types';
import useTrackingFilterDrawerCategories from './useTrackingFilterDrawerCategories';
import useTrackingFilterHistoryCategories, {useIsTrackingHistoryRunning} from './useTrackingFilterHistoryCategories';
import useTrackingFilterLimitWarning from './useTrackingFilterLimitWarning';

export interface TrackingFilterDrawerProps {
  desktopOffset?: number;
  onClose: () => void;
  open: boolean;
}

interface StyleProps {
  drawerOffset: number;
  isPhone: boolean;
  mobileTopOffset: number;
}

// the footer's heart - with a pencil while the selected favorite has unsaved
// changes, filled while one is selected ...
const getFooterFavoriteState = (
  selectedFavoriteName: string | null,
  dirtyFavoriteName: string | null | false | undefined
): AppliedFilterBarFavoriteState => {
  let state = AppliedFilterBarFavoriteState.None;
  if (dirtyFavoriteName) {
    state = AppliedFilterBarFavoriteState.Working;
  } else if (selectedFavoriteName) {
    state = AppliedFilterBarFavoriteState.Saved;
  }
  return state;
};

const useStyles = makeStyles(() => ({
  warningDialogActions : {
    display        : 'flex',
    justifyContent : 'space-between',
    padding        : '0 18px 8px !important',
    width          : 'calc(100% - 42px)',
    '& > div'      : {
      width : 'auto'
    }
  },
  warningDialogRoot : {
    height : ({isPhone, mobileTopOffset}: StyleProps) => (
      isPhone ? `calc(100vh - ${mobileTopOffset}px) !important` : '100vh !important'
    ),
    left : ({drawerOffset, isPhone}: StyleProps) => (
      `${isPhone ? 0 : drawerOffset}px !important`
    ),
    right : 'auto !important',
    top   : ({isPhone, mobileTopOffset}: StyleProps) => (
      `${isPhone ? mobileTopOffset : 0}px !important`
    ),
    width : ({drawerOffset, isPhone}: StyleProps) => (
      isPhone
        ? '100vw !important'
        : `min(${getFilterDrawerWidth()}px, calc(100vw - ${drawerOffset}px)) !important`
    )
  }
}));

export const TrackingFilterDrawer = forwardRef<TrackingFilterDrawerHandle, TrackingFilterDrawerProps>(({
  desktopOffset,
  onClose,
  open
}, ref) => {
  const dispatch               = useDispatch();
  const assetDetailView        = useSelector(assetDetailViewSelector);
  const historyPreset          = useSelector(historyPresetSelector);
  const historyFrom            = useSelector(historyFromSelector);
  const historyTo              = useSelector(historyToSelector);
  const boundary               = useSelector(boundaryFilterSelector);
  const boundaryMode           = useSelector(boundaryModeSelector);
  const isHistoryRunning       = useIsTrackingHistoryRunning();
  // the drawer is built before its own copy has loaded - the side nav holds
  // it from the start - so it is drawn again once that copy is in ...
  const {ready: isCopyLoaded}  = useTranslation('filters');
  const applyHistoryTimeMode   = useApplyHistoryTimeMode();
  const returnToLiveTracking   = useReturnToLiveTracking();
  const [, resetTracking]      = useSelectAssetAndResets();
  const {onApplyBoundary}      = useBoundaryFilter({});
  const {isPhone}              = useMedia();
  const drawerOffset           = desktopOffset ?? dlsSidebarDrawerLeft(true);
  const mobileTopOffset        = dlsLayout('header').mobile;
  const classes                = useStyles({drawerOffset, isPhone, mobileTopOffset});
  const expandedIds            = useSyncExternalStore(
    subscribeTrackingFilterExpandedIds,
    getTrackingFilterExpandedIds,
    getTrackingFilterExpandedIds
  );
  const categoryOrder = useSyncExternalStore(
    subscribeTrackingFilterCategoryOrder,
    getTrackingFilterCategoryOrder,
    getTrackingFilterCategoryOrder
  );
  const favorites = useSyncExternalStore(
    subscribeTrackingFilterFavorites,
    getTrackingFilterFavorites,
    getTrackingFilterFavorites
  );
  const workingFavorite = useSyncExternalStore(
    subscribeTrackingWorkingFavorite,
    getTrackingWorkingFavorite,
    getTrackingWorkingFavorite
  );
  useEffect(() => {
    initializeTrackingFilterCategoryOrder();
    initializeTrackingFilterFavorites();
  }, []);
  // the applied filter bar steps aside from the drawer while it is open ...
  useEffect(() => {
    setTrackingFilterDrawerOpen(open);
    return function() {
      setTrackingFilterDrawerOpen(false);
    };
  }, [open]);

  const loadingFavoriteRef     = useRef(false);
  const syncedDraftRevisionRef = useRef(0);
  // the saved state of a favorite loaded into the draft - its time mode and
  // boundary are applied to the map's own widgets with the draft, as a legacy
  // saved filter's are ...
  const pendingFavoriteRef     = useRef<TrackingFavoriteFilterState | null>(null);
  // what the user was doing when asked about the unapplied changes - a
  // drawer close (run once the changes are applied or discarded) or a
  // navigation away from Tracking (run once the user leaves) ...
  const pendingCloseRef = useRef<(() => void) | null>(null);
  const pendingLeaveRef = useRef<(() => void) | null>(null);
  // the filters' own links away from Tracking go through the same leave
  // guard as the side nav; the filters are built before it is, so they
  // reach it through this ref ...
  const confirmLeaveRef = useRef<TrackingFilterNavigationRequest | null>(null);

  const [showClearAllConfirmation, setShowClearAllConfirmation]     = useState(false);
  const [applyClearedDraft, setApplyClearedDraft]                   = useState(false);
  const [showLeaveConfirmation, setShowLeaveConfirmation]           = useState(false);
  const [showUnappliedChanges, setShowUnappliedChanges]             = useState(false);
  const [favoriteDialog, setFavoriteDialog]                         =
    useState<FavoriteDialogState | null>(null);
  const [deleteFavoriteName, setDeleteFavoriteName]                  = useState<string | null>(null);
  const [favoriteSnackbarMessage, setFavoriteSnackbarMessage]        = useState<string | null>(null);
  const [draftRevision, setDraftRevision]                            = useState(0);

  const markDraftChanged = useCallback(() => {
    if (!loadingFavoriteRef.current) {
      setDraftRevision(revision => revision + 1);
    }
  }, []);

  const requestNavigate = useCallback(function(navigate: () => void) {
    confirmLeaveRef.current?.(navigate);
  }, []);

  const {
    counts,
    filters,
    getFilterHandles,
    selectedFiltersCount
  } = useTrackingFilterDrawerCategories({
    onRequestNavigate : requestNavigate,
    onSelectionChange : markDraftChanged
  });

  // the History range (or Live) and the boundary on the map now - a favorite
  // keeps them with its filters ...
  const widgetState = useMemo(() => getTrackingFavoriteWidgetState({
    isHistoryRunning,
    historyPreset,
    historyFrom,
    historyTo,
    boundary,
    boundaryMode
  }), [boundary, boundaryMode, historyFrom, historyPreset, historyTo, isHistoryRunning]);

  // a favorite loaded but not yet applied counts as well while its time mode
  // or boundary differ from the map's - those are applied with the draft,
  // so leaving it unapplied would leave them out of step with it ...
  const hasUnappliedChanges = useCallback(function() {
    const pendingFavorite = pendingFavoriteRef.current;
    return getFilterHandles().some(handle => handle.hasChanges()) ||
      Boolean(pendingFavorite && !areTrackingFavoriteWidgetsEqual(pendingFavorite, widgetState));
  }, [getFilterHandles, widgetState]);

  // the favorite selection goes back with the filters - to the favorite they
  // were applied under, while it is still saved, or to none ...
  const discardDraft = useCallback(function() {
    const appliedFavorite = getTrackingAppliedFavorite();
    pendingFavoriteRef.current = null;
    getFilterHandles().forEach(handle => {
      handle.discard();
    });
    setTrackingWorkingFavorite(appliedFavorite && favorites[appliedFavorite.name] ? appliedFavorite : null);
  }, [favorites, getFilterHandles]);

  // runs the held drawer close exactly once ...
  const runPendingClose = useCallback(function() {
    const continueAction = pendingCloseRef.current;
    pendingCloseRef.current = null;
    continueAction?.();
  }, []);

  // hides every prompt, so the one about to be shown replaces it ...
  const hidePrompts = useCallback(function() {
    setShowClearAllConfirmation(false);
    setShowLeaveConfirmation(false);
    setShowUnappliedChanges(false);
    setFavoriteDialog(null);
    setDeleteFavoriteName(null);
  }, []);

  const collectFavoriteState = useCallback(() => (
    normalizeTrackingFavoriteState(getFilterHandles().reduce((state, handle) => ({
      ...state,
      ...(handle.getFavoriteState?.() ?? {})
    }), {} as TrackingFavoriteFilterState))
  ), [getFilterHandles]);

  const currentFavoriteState = useMemo(() => (
    collectFavoriteState()
  ), [
    collectFavoriteState,
    draftRevision,
    selectedFiltersCount
  ]);

  // while a Timeline or History report is running, the draft is checked
  // against its limit - over it, the footer warns and Apply is held back,
  // so the report keeps showing what it shows ...
  const {limitWarning, isCheckingLimit, checkLimitNow} = useTrackingFilterLimitWarning({
    open,
    draftState : currentFavoriteState
  });

  const selectedFavoriteName       = workingFavorite?.name ?? null;
  const selectedFavoriteSavedState = selectedFavoriteName
    ? favorites[selectedFavoriteName]
    : null;
  // a favorite reads as changed once its filters, or the History range or
  // boundary on the map, differ from what it was saved with - its widgets
  // not until it is applied, which is when they are put on the map ...
  const dirtyFavoriteName = selectedFavoriteName && selectedFavoriteSavedState && (
    !areTrackingFavoriteStatesEqual(workingFavorite?.state ?? {}, selectedFavoriteSavedState) ||
    (!pendingFavoriteRef.current && !areTrackingFavoriteWidgetsEqual(selectedFavoriteSavedState, widgetState))
  )
    ? selectedFavoriteName
    : null;

  useEffect(() => {
    if (draftRevision === syncedDraftRevisionRef.current) {
      return;
    }
    if (selectedFavoriteName && selectedFavoriteSavedState &&
      !areTrackingFavoriteStatesEqual(currentFavoriteState, workingFavorite?.state ?? {})) {
      syncedDraftRevisionRef.current = draftRevision;
      setTrackingWorkingFavorite({
        name  : selectedFavoriteName,
        state : currentFavoriteState
      });
    }
  }, [
    currentFavoriteState,
    draftRevision,
    selectedFavoriteName,
    selectedFavoriteSavedState,
    workingFavorite?.state
  ]);

  // puts a favorite's time mode and boundary on the map's own widgets - its
  // boundary first, so a History report it runs is run within it. A boundary
  // it does not keep is cleared the way the map's own vicinity search X
  // clears one, its card, drawing tool and search closing with it ...
  const applyFavoriteWidgets = useCallback(function(favorite: TrackingFavoriteFilterState) {
    if (hasTrackingFavoriteBoundary(favorite)) {
      const rings = favorite.boundary;
      if (rings.length) {
        dispatch(setBoundaryFilterMode(favorite.boundaryMode ?? BoundaryMode.Boundary));
        dispatch(applyBoundaryFilter(rings) as any);
      } else if (boundary?.length) {
        onApplyBoundary([]);
      }
    }
    const historyRange = getTrackingFavoriteHistoryRange(favorite);
    if (historyRange) {
      const {preset, from, to} = historyRange;
      dispatch(applyAssetHistoryDateFilter({fromDate: from, toDate: to, selectedRadio: preset}) as any);
      applyHistoryTimeMode({preset, from, to});
    } else if (isTrackingFavoriteLive(favorite) && isHistoryRunning) {
      returnToLiveTracking();
    }
  }, [applyHistoryTimeMode, boundary, dispatch, isHistoryRunning, onApplyBoundary, returnToLiveTracking]);

  // applies the draft to the app, leaving the drawer as it is ...
  const commitDraft = useCallback(() => {
    getFilterHandles().forEach(handle => {
      handle.apply();
    });
    setTrackingAppliedFavorite(getTrackingWorkingFavorite());
    dispatch(setSelectedFiltersActive(!!selectedFiltersCount) as any);
    const pendingFavorite = pendingFavoriteRef.current;
    pendingFavoriteRef.current = null;
    if (pendingFavorite) {
      applyFavoriteWidgets(pendingFavorite);
    }
    // the open asset may not be in the newly filtered list, so its details
    // close (and it is deselected), back to the list or grid it came from ...
    if (assetDetailView) {
      resetTracking(true);
    }
  }, [
    applyFavoriteWidgets,
    assetDetailView,
    dispatch,
    getFilterHandles,
    resetTracking,
    selectedFiltersCount
  ]);

  const applyDraft = useCallback(() => {
    commitDraft();
    setShowClearAllConfirmation(false);
    setShowUnappliedChanges(false);
    onClose();
    runPendingClose();
  }, [commitDraft, onClose, runPendingClose]);

  // held back while the draft is over a running report's limit - the footer's
  // Apply is disabled then, and this also turns away the unapplied changes
  // dialog's own Apply. Apply stays live while the latest draft's count is
  // under way, so it does not flicker with every check; asked then, the draft
  // is checked at once and only applied once it is through ...
  const applyFilters = useCallback(async() => {
    const warning = isCheckingLimit ? await checkLimitNow() : limitWarning;
    if (!warning) {
      applyDraft();
    }
  }, [applyDraft, checkLimitNow, isCheckingLimit, limitWarning]);

  // a confirmed Clear All clears the filters from the app, as Apply does. The
  // prompt and the drawer go at once - clearing every filter (a large tree
  // among them) and applying that is the long part, so it runs once the
  // closed drawer has painted, the filters held mounted meanwhile. The clear
  // is applied on the render after, once each filter's cleared draft is what
  // its apply reads ...
  const clearFilters = useCallback(() => {
    pendingFavoriteRef.current = null;
    setTrackingWorkingFavorite(null);
    setShowClearAllConfirmation(false);
    onClose();
    afterNextPaint().then(function() {
      getFilterHandles().forEach(handle => {
        handle.clear();
      });
      setApplyClearedDraft(true);
    });
  }, [getFilterHandles, onClose]);

  useEffect(() => {
    if (applyClearedDraft) {
      setApplyClearedDraft(false);
      applyDraft();
    }
  }, [applyClearedDraft, applyDraft]);

  const loadFavoriteState = useCallback((name: string, state: TrackingFavoriteFilterState) => {
    const normalizedState = normalizeTrackingFavoriteState(state);
    // a favorite's unsaved changes keep only its filters, so its time mode
    // and boundary are read from what it was saved with ...
    pendingFavoriteRef.current = favorites[name] ?? state;
    loadingFavoriteRef.current = true;
    syncedDraftRevisionRef.current = draftRevision;
    try {
      getFilterHandles().forEach(handle => {
        handle.loadFavoriteState?.(normalizedState);
      });
      setTrackingWorkingFavorite({
        name,
        state : normalizedState
      });
    } finally {
      loadingFavoriteRef.current = false;
    }
    setTrackingFilterExpandedIds([
      TrackingFilterDrawerCategoryId.Favorites,
      ...getTrackingFavoriteSelectedCategoryIds(normalizedState)
    ]);
  }, [draftRevision, favorites, getFilterHandles]);

  // a favorite keeps the time mode it was saved in, as a legacy saved filter
  // does - the range a running History report covers, or Live ...
  const collectFavoriteStateToSave = useCallback((): TrackingFavoriteFilterState => {
    const state = collectFavoriteState();
    return {...state, ...widgetState};
  }, [collectFavoriteState, widgetState]);

  const getFavoriteErrorText = useCallback((error?: TrackingFavoriteNameValidationError | null) => {
    const translations = {
      [TrackingFavoriteNameValidationError.Duplicate] : translate(
        'filters:tracking.favorites.validation.duplicate'
      ),
      [TrackingFavoriteNameValidationError.Empty] : translate(
        'filters:tracking.favorites.validation.empty'
      ),
      [TrackingFavoriteNameValidationError.MaxLength] : translate(
        'filters:tracking.favorites.validation.maxLength'
      )
    };
    return error ? translations[error] : undefined;
  }, []);

  const closeFavoriteDialog = useCallback(() => {
    setFavoriteDialog(null);
  }, []);

  const closeFavoriteSnackbar = useCallback(() => {
    setFavoriteSnackbarMessage(null);
  }, []);

  // a running History report is worth keeping on its own, filters or not ...
  const openCreateFavoriteDialog = useCallback(() => {
    if (selectedFiltersCount || isHistoryRunning || boundary?.length) {
      setFavoriteDialog({
        type : FavoriteDialogType.Create
      });
    } else {
      setFavoriteSnackbarMessage(translate('filters:tracking.favorites.snackbar.selectOne'));
    }
  }, [boundary, isHistoryRunning, selectedFiltersCount]);

  const openRenameFavoriteDialog = useCallback((name: string) => {
    setFavoriteDialog({
      name,
      type : FavoriteDialogType.Rename
    });
  }, []);

  const openEditFavoriteDialog = useCallback((name: string) => {
    setFavoriteDialog({
      name,
      type : FavoriteDialogType.Edit
    });
  }, []);

  const openSaveAsNewFavoriteDialog = useCallback((name: string) => {
    setFavoriteDialog({
      name : translate('filters:tracking.favorites.dialog.copyName', {name}),
      type : FavoriteDialogType.SaveAsNew
    });
  }, []);

  const validateDialogName = useCallback((name: string, currentName?: string | null) => (
    validateTrackingFavoriteName(name, favorites, currentName)
  ), [favorites]);

  const saveNewFavorite = useCallback((name: string, snackbarMessage: string) => {
    const validationError = validateDialogName(name);
    if (validationError) {
      setFavoriteDialog(dialog => dialog ? {...dialog, error: validationError} : dialog);
    } else {
      const favoriteName = trimTrackingFavoriteName(name);
      const favoriteState = collectFavoriteStateToSave();
      saveTrackingFilterFavorite(favoriteName, favoriteState);
      setTrackingWorkingFavorite({
        name  : favoriteName,
        state : favoriteState
      });
      closeFavoriteDialog();
      setFavoriteSnackbarMessage(snackbarMessage);
    }
  }, [closeFavoriteDialog, collectFavoriteStateToSave, validateDialogName]);

  const updateFavorite = useCallback((name: string) => {
    const currentName = favoriteDialog?.name;
    const validationError = validateDialogName(name, currentName);
    if (validationError) {
      setFavoriteDialog(dialog => dialog ? {...dialog, error: validationError} : dialog);
    } else if (currentName) {
      const nextName = trimTrackingFavoriteName(name);
      const favoriteState = collectFavoriteStateToSave();
      if (nextName !== currentName) {
        renameTrackingFilterFavorite(currentName, nextName);
      }
      saveTrackingFilterFavorite(nextName, {
        ...(favorites[currentName] ?? {}),
        ...favoriteState
      });
      setTrackingWorkingFavorite({
        name  : nextName,
        state : favoriteState
      });
      closeFavoriteDialog();
      setFavoriteSnackbarMessage(translate('filters:tracking.favorites.snackbar.updated'));
    }
  }, [
    closeFavoriteDialog,
    collectFavoriteStateToSave,
    favoriteDialog?.name,
    favorites,
    validateDialogName
  ]);

  const renameFavorite = useCallback((name: string) => {
    const currentName = favoriteDialog?.name;
    const validationError = validateDialogName(name, currentName);
    if (validationError) {
      setFavoriteDialog(dialog => dialog ? {...dialog, error: validationError} : dialog);
    } else if (currentName) {
      const nextName = trimTrackingFavoriteName(name);
      renameTrackingFilterFavorite(currentName, nextName);
      if (selectedFavoriteName === currentName) {
        setTrackingWorkingFavorite({
          name  : nextName,
          state : workingFavorite?.state ?? favorites[currentName]
        });
      }
      closeFavoriteDialog();
      setFavoriteSnackbarMessage(translate('filters:tracking.favorites.snackbar.updated'));
    }
  }, [
    closeFavoriteDialog,
    favoriteDialog?.name,
    favorites,
    selectedFavoriteName,
    validateDialogName,
    workingFavorite?.state
  ]);

  // a reset goes back to the favorite's saved filters, time mode and boundary
  // alike - all applied with Apply, as any other change in the drawer ...
  const resetFavorite = useCallback((name: string) => {
    const savedState = favorites[name];
    if (savedState) {
      loadFavoriteState(name, savedState);
      setFavoriteSnackbarMessage(translate('filters:tracking.favorites.snackbar.reset', {name}));
    }
  }, [favorites, loadFavoriteState]);

  const deleteFavorite = useCallback(() => {
    if (deleteFavoriteName) {
      deleteTrackingFilterFavorite(deleteFavoriteName);
      if (selectedFavoriteName === deleteFavoriteName) {
        setTrackingWorkingFavorite(null);
      }
      setDeleteFavoriteName(null);
      setFavoriteSnackbarMessage(translate('filters:tracking.favorites.snackbar.deleted'));
    }
  }, [deleteFavoriteName, selectedFavoriteName]);

  const selectFavorite = useCallback((name: string, state: TrackingFavoriteFilterState) => {
    loadFavoriteState(name, workingFavorite?.name === name
      ? workingFavorite.state
      : state
    );
  }, [loadFavoriteState, workingFavorite]);

  // editing the favorite already selected keeps its unsaved changes on screen
  // - its edit saves them, as Save Changes and the footer's heart do. Any
  // other favorite is loaded first, so its edit starts from its saved state ...
  const editFavorite = useCallback((name: string) => {
    const state = favorites[name];
    if (state) {
      if (name !== selectedFavoriteName) {
        loadFavoriteState(name, state);
      }
      openEditFavoriteDialog(name);
    }
  }, [favorites, loadFavoriteState, openEditFavoriteDialog, selectedFavoriteName]);

  const requestFavoriteDelete = useCallback((name: string) => {
    setDeleteFavoriteName(name);
  }, []);

  const requestFooterFavorite = useCallback(() => {
    if (selectedFavoriteName) {
      openEditFavoriteDialog(selectedFavoriteName);
    } else {
      openCreateFavoriteDialog();
    }
  }, [openCreateFavoriteDialog, openEditFavoriteDialog, selectedFavoriteName]);

  const handleFavoriteDialogPrimaryAction = useCallback((name: string) => {
    if (favoriteDialog?.type === FavoriteDialogType.Create) {
      saveNewFavorite(name, translate('filters:tracking.favorites.snackbar.saved'));
    } else if (favoriteDialog?.type === FavoriteDialogType.Rename) {
      renameFavorite(name);
    } else if (favoriteDialog?.type === FavoriteDialogType.Edit) {
      updateFavorite(name);
    } else if (favoriteDialog?.type === FavoriteDialogType.SaveAsNew) {
      saveNewFavorite(name, translate('filters:tracking.favorites.snackbar.created'));
    }
  }, [favoriteDialog?.type, renameFavorite, saveNewFavorite, updateFavorite]);

  const handleFavoriteDialogSecondaryAction = useCallback(() => {
    if (favoriteDialog?.name) {
      openSaveAsNewFavoriteDialog(favoriteDialog.name);
    }
  }, [favoriteDialog?.name, openSaveAsNewFavoriteDialog]);

  const requestClearFilters = useCallback(() => {
    setShowClearAllConfirmation(true);
  }, []);

  const dismissClearAllConfirmation = useCallback(() => {
    setShowClearAllConfirmation(false);
  }, []);

  const requestClose = useCallback(() => {
    pendingCloseRef.current = null;
    if (hasUnappliedChanges()) {
      setShowUnappliedChanges(true);
    } else {
      onClose();
    }
  }, [hasUnappliedChanges, onClose]);

  const discardChanges = useCallback(() => {
    discardDraft();
    setShowUnappliedChanges(false);
    onClose();
    runPendingClose();
  }, [discardDraft, onClose, runPendingClose]);

  const dismissUnappliedChanges = useCallback(() => {
    pendingCloseRef.current = null;
    setShowUnappliedChanges(false);
  }, []);

  // an action that would close the open drawer while staying on Tracking
  // waits on the Apply/Discard prompt when there are unapplied changes ...
  const confirmClose = useCallback(function(continueAction: () => void) {
    if (open && hasUnappliedChanges()) {
      hidePrompts();
      pendingLeaveRef.current = null;
      pendingCloseRef.current = continueAction;
      setShowUnappliedChanges(true);
    } else {
      continueAction();
    }
  }, [hasUnappliedChanges, hidePrompts, open]);

  // a navigation away from Tracking waits on the Leave Tracking prompt when
  // the open drawer has unapplied changes ...
  const confirmLeave = useCallback(function(leave: () => void) {
    if (open && hasUnappliedChanges()) {
      hidePrompts();
      pendingCloseRef.current = null;
      pendingLeaveRef.current = leave;
      setShowLeaveConfirmation(true);
    } else {
      leave();
    }
  }, [hasUnappliedChanges, hidePrompts, open]);

  confirmLeaveRef.current = confirmLeave;

  // Stay leaves the drawer, its changes and the navigation as they are ...
  const stayOnTracking = useCallback(function() {
    pendingLeaveRef.current = null;
    setShowLeaveConfirmation(false);
  }, []);

  // Leave Tracking discards the changes, closes the drawer, then runs the
  // held navigation exactly once ...
  const leaveTracking = useCallback(function() {
    const leave = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    discardDraft();
    setShowLeaveConfirmation(false);
    onClose();
    leave?.();
  }, [discardDraft, onClose]);

  // discards the changes without closing the drawer - for a navigation away
  // from Tracking that was not asked about first, such as browser back ...
  const discardUnappliedChanges = useCallback(function() {
    pendingCloseRef.current = null;
    pendingLeaveRef.current = null;
    discardDraft();
    setShowLeaveConfirmation(false);
    setShowUnappliedChanges(false);
  }, [discardDraft]);

  useImperativeHandle(ref, () => ({
    confirmClose,
    confirmLeave,
    discardChanges : discardUnappliedChanges
  }), [confirmClose, confirmLeave, discardUnappliedChanges]);

  // while the drawer is open, a refresh or tab close with unapplied changes
  // asks the browser to confirm leaving the site. It is registered as a
  // listener, since UserPreferencesAutoSave owns onbeforeunload ...
  useEffect(() => {
    const onBeforeUnload = function(event: BeforeUnloadEvent) {
      if (hasUnappliedChanges()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    if (open) {
      window.addEventListener('beforeunload', onBeforeUnload);
    }
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [hasUnappliedChanges, open]);

  const favoritesFilter = useMemo(() => (
    <TrackingFavoritesFilter favorites={favorites}
      selectedFavoriteName={selectedFavoriteName}
      dirtyFavoriteName={dirtyFavoriteName}
      onDelete={requestFavoriteDelete}
      onEdit={editFavorite}
      onRename={openRenameFavoriteDialog}
      onReset={resetFavorite}
      onSaveChanges={openEditFavoriteDialog}
      onSelect={selectFavorite} />
  ), [
    dirtyFavoriteName,
    editFavorite,
    favorites,
    openEditFavoriteDialog,
    openRenameFavoriteDialog,
    requestFavoriteDelete,
    resetFavorite,
    selectFavorite,
    selectedFavoriteName
  ]);
  const categories = useMemo<FilterDrawerCategory[]>(() => ([{
    id      : TrackingFilterDrawerCategoryId.Favorites,
    label   : translate('filters:tracking.categories.favorites'),
    content : favoritesFilter
  }, {
    id            : TrackingFilterDrawerCategoryId.GroupsAssets,
    label         : translate('filters:tracking.categories.groupsAssets'),
    selectedCount : counts.groupsAssets,
    content       : filters.groupsAssets
  }, {
    id            : TrackingFilterDrawerCategoryId.AssetType,
    label         : translate('filters:tracking.categories.assetType'),
    selectedCount : counts.assetType,
    content       : filters.assetType
  }, {
    id            : TrackingFilterDrawerCategoryId.Geofence,
    label         : translate('filters:tracking.categories.geofence'),
    selectedCount : counts.geofence,
    content       : filters.geofence
  }, {
    id            : TrackingFilterDrawerCategoryId.Routes,
    label         : translate('filters:tracking.categories.routes'),
    selectedCount : counts.route,
    content       : filters.route
  }, {
    id            : TrackingFilterDrawerCategoryId.Category,
    label         : translate('filters:tracking.categories.category'),
    selectedCount : counts.assetCategory,
    content       : filters.assetCategory
  }, {
    id            : TrackingFilterDrawerCategoryId.AssetStatus,
    label         : translate('filters:tracking.categories.assetStatus'),
    selectedCount : counts.assetStatus,
    content       : filters.assetStatus
  }]), [
    counts.assetCategory,
    counts.assetStatus,
    counts.assetType,
    counts.geofence,
    counts.groupsAssets,
    counts.route,
    favoritesFilter,
    filters.assetCategory,
    filters.assetStatus,
    filters.assetType,
    filters.geofence,
    filters.groupsAssets,
    filters.route,
    isCopyLoaded
  ]);

  // while History runs (current theme only), Geofence, Routes and Asset
  // Status are hidden - their filters kept mounted out of sight, so the
  // selections they hold still take part in Apply, Clear All and favorites ...
  const {
    categories            : shownCategories,
    expandedIds           : shownExpandedIds,
    hiddenContent         : hiddenFilters,
    onCategoryOrderChange : setShownCategoryOrder,
    onExpandedIdsChange   : setShownExpandedIds
  } = useTrackingFilterHistoryCategories({categories, categoryOrder, expandedIds});

  const footerMessage = useMemo(() => (
    limitWarning ? <TimeModeLimitWarning {...limitWarning} /> : null
  ), [limitWarning]);

  const cancelFavoriteDelete = useCallback(() => {
    setDeleteFavoriteName(null);
  }, []);

  const favoriteDialogLabels = useMemo<FavoriteDialogLabels>(() => {
    const labels = {
      cancel       : translate('filters:tracking.favorites.dialog.cancel'),
      description  : translate('filters:tracking.favorites.dialog.createDescription'),
      inputLabel   : translate('filters:tracking.favorites.dialog.inputLabel'),
      placeholder  : translate('filters:tracking.favorites.dialog.placeholder'),
      primaryLabel : translate('filters:tracking.favorites.dialog.create'),
      title        : translate('filters:tracking.favorites.dialog.createTitle')
    };
    if (favoriteDialog?.type === FavoriteDialogType.Rename) {
      labels.description = translate('filters:tracking.favorites.dialog.renameDescription');
      labels.primaryLabel = translate('filters:tracking.favorites.dialog.save');
      labels.title = translate('filters:tracking.favorites.dialog.renameTitle');
    } else if (favoriteDialog?.type === FavoriteDialogType.Edit) {
      labels.description = translate('filters:tracking.favorites.dialog.editDescription');
      labels.primaryLabel = translate('filters:tracking.favorites.dialog.saveChanges');
      labels.title = translate('filters:tracking.favorites.dialog.editTitle');
    } else if (favoriteDialog?.type === FavoriteDialogType.SaveAsNew) {
      labels.description = translate('filters:tracking.favorites.dialog.saveAsNewDescription');
      labels.primaryLabel = translate('filters:tracking.favorites.dialog.saveAsNew');
      labels.title = translate('filters:tracking.favorites.dialog.saveAsNewTitle');
    }
    return labels;
  }, [favoriteDialog?.type, isCopyLoaded]);

  return (
    <>
      <FilterDrawer key={isCopyLoaded ? 'copy-loaded' : 'copy-loading'}
        open={open}
        applyDisabled={!!limitWarning}
        title={translate('filters:tracking.title')}
        categoryOrder={categoryOrder}
        categories={shownCategories}
        clearAllDisabled={!selectedFiltersCount}
        desktopOffset={desktopOffset}
        expandedIds={shownExpandedIds}
        favorite={!!selectedFavoriteName}
        favoriteState={getFooterFavoriteState(selectedFavoriteName, dirtyFavoriteName)}
        footerMessage={footerMessage}
        onApply={applyFilters}
        onCategoryOrderChange={setShownCategoryOrder}
        onClearAll={requestClearFilters}
        onClose={requestClose}
        onExpandedIdsChange={setShownExpandedIds}
        onFavoriteChange={requestFooterFavorite} />
      <div hidden>
        {hiddenFilters}
      </div>
      <TrackingFilterDrawerDialogs actionsClassName={classes.warningDialogActions}
        applyDisabled={!!limitWarning}
        rootClassName={classes.warningDialogRoot}
        showClearAllConfirmation={showClearAllConfirmation}
        showLeaveConfirmation={showLeaveConfirmation}
        showUnappliedChanges={showUnappliedChanges}
        favoriteDialog={favoriteDialog}
        favoriteDialogLabels={favoriteDialogLabels}
        deleteFavoriteName={deleteFavoriteName}
        favoriteSnackbarMessage={favoriteSnackbarMessage}
        getFavoriteErrorText={getFavoriteErrorText}
        onApplyFilters={applyFilters}
        onClearFilters={clearFilters}
        onCloseFavoriteDialog={closeFavoriteDialog}
        onCloseFavoriteSnackbar={closeFavoriteSnackbar}
        onDeleteFavorite={deleteFavorite}
        onDismissClearAllConfirmation={dismissClearAllConfirmation}
        onDismissUnappliedChanges={dismissUnappliedChanges}
        onDiscardChanges={discardChanges}
        onFavoriteDialogPrimaryAction={handleFavoriteDialogPrimaryAction}
        onFavoriteDialogSecondaryAction={handleFavoriteDialogSecondaryAction}
        onLeaveTracking={leaveTracking}
        onRequestFavoriteDeleteCancel={cancelFavoriteDelete}
        onStayOnTracking={stayOnTracking} />
    </>
  );
});

export default memo(TrackingFilterDrawer);
